import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import api from "@/lib/axios"
import { getListJalurJenis, getListJenisUkt } from "@/services/getData"
import { JalurJenis, JenisUkt } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { LuDownload } from "react-icons/lu"
import { RiFileExcel2Fill } from "react-icons/ri"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

type FormImportNominalUKT = {
  id_ref_jalur_jenis: string
  id_ref_jenis_ukt: string
  tahun_masuk: string
  file: File | null
}

const postData = async (newData: FormData) => {
  const response = await api.post("/master/nominal-ukt/import", newData)
  return response.data
}

export default function FormImport({ closeModal }: { closeModal: () => void }) {
  const [form, setForm] = useState<FormImportNominalUKT>({
    id_ref_jalur_jenis: "",
    id_ref_jenis_ukt: "",
    tahun_masuk: `${new Date().getFullYear()}` || "",
    file: null,
  })
  const { data: dataJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  })
  const { data: dataJenisUkt } = useQuery({
    queryKey: ["listJenisUkt"],
    queryFn: getListJenisUkt,
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: postData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listNominalUkt"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const formData = new FormData()
    formData.append("id_ref_jalur_jenis", form.id_ref_jalur_jenis)
    formData.append("id_ref_jenis_ukt", form.id_ref_jenis_ukt)
    formData.append("tahun_masuk", form.tahun_masuk)
    if (form.file) formData.append("file", form.file)

    mutate(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader title="Import Data Nominal UKT" icon={<RiFileExcel2Fill />} />

      <div>
        <Label>Jalur Jenis</Label>
        <Select
          value={form.id_ref_jalur_jenis}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              id_ref_jalur_jenis: value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jalur Jenis">
              {form.id_ref_jalur_jenis &&
                dataJalurJenis?.find(
                  (jalurJenis: JalurJenis) =>
                    jalurJenis.id_ref_jalur_jenis === form.id_ref_jalur_jenis
                )?.nama_jalur_jenis}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {dataJalurJenis?.map((jalurJenis: JalurJenis, index: number) => (
              <SelectItem
                key={index}
                value={`${jalurJenis.id_ref_jalur_jenis}`}
              >
                {jalurJenis?.nama_jalur_jenis}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Jenis UKT</Label>
        <Select
          value={form.id_ref_jenis_ukt}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              id_ref_jenis_ukt: value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jalur Jenis">
              {form.id_ref_jenis_ukt &&
                dataJenisUkt?.find(
                  (jenisUkt: JenisUkt) =>
                    jenisUkt.id_ref_jenis_ukt === form.id_ref_jenis_ukt
                )?.nama_jenis_ukt}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {dataJenisUkt?.map((jenisUkt: JenisUkt, index: number) => (
              <SelectItem key={index} value={`${jenisUkt.id_ref_jenis_ukt}`}>
                {jenisUkt?.nama_jenis_ukt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Tahun Masuk</Label>
        <Input
          required
          minLength={4}
          maxLength={4}
          placeholder="Ketik tahun..."
          value={form.tahun_masuk}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, tahun_masuk: event.target.value }))
          }
        />
      </div>
      <div>
        <Label>Format Excel</Label>
        <div>
          <Button
            type="button"
            className="w-full flex items-center gap-3"
            variant={"outline"}
            onClick={() => {
              window.open("/format/format_import_ukt.xlsx")
            }}
          >
            <LuDownload /> Unduh Format Import
          </Button>
        </div>
      </div>
      <div>
        <Label>File Excel</Label>
        <Input
          required
          type="file"
          onChange={(event) => {
            const files = event.target.files
            if (files && files[0]) {
              setForm((prev) => ({ ...prev, file: files[0] }))
            }
          }}
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
